import Vue from 'vue'
import contactApi from '@/api/contact.api'
import { getAvatarChar, getRandomAvatarColor } from '@/helpers/avatar.helper'
import { phoneType, emailType } from '@/constants'
import i18n from '@/plugins/i18n'

const state = {
  shortContacts: {},
  contactsTableItems: {},
  contactsTableItemsForMerge: {},
  contactsTableTotalCount: 0,
  contactsTableTotalCountForMerge: 0,
  mergeContactsTableTotalCount: 0,
  duplicatesTableItems: {},
  duplicatesTableItemsTotalCount: 0,
  phoneContacts: [],
  phoneContactsMobile: [],
  contactsById: {},
  addressTypes: [],
  emailTypes: [],
  phoneTypes: []
}


const getters = {
  shortContacts: (state) => {
    return Object.values(state.shortContacts)
  },

  shortContactsWithEmail: (state) => {
    return Object.values(state.shortContacts).filter(contact => contact.email !== null)
  },


  contactsTableItems: (state) => {
    return Object.values(state.contactsTableItems).map(item => {
      const displayEmails = []
      if (item.emails && item.emails.length) {
        item.emails.forEach(itemEmail => {
          let name = ''
          switch (itemEmail.emailType.name) {
            case emailType.work:
              name = i18n.t('emailType.work')
              break
            case emailType.personal:
              name = i18n.t('emailType.personal')
              break
          }
          let emailString = `${itemEmail.email} (${name})`

          if (itemEmail.isPrimary) {
            emailString += ' *'
          }

          displayEmails.push(emailString)
        })
      }

      const displayPhones = []
      if (item.phones && item.phones.length) {
        item.phones.forEach(itemPhone => {
          let name = ''
          switch (itemPhone.phoneType.name) {
            case phoneType.mobile:
              name = i18n.t('phoneType.mobile')
              break
            case phoneType.work:
              name = i18n.t('phoneType.work')
              break
            case phoneType.home:
              name = i18n.t('phoneType.home')
              break
          }
          let phoneString = `${itemPhone.phone} (${name})`

          if (itemPhone.isPrimary) {
            phoneString += ' *'
          }

          displayPhones.push(phoneString)
        })
      }

      const iceContact = {
        name: item.iceName,
        phone: item.icePhone
      }

      return {
        ...item,
        avatarChar: getAvatarChar(item.firstName, item.lastName, item.company, item.email?.email),
        displayEmails,
        displayPhones,
        iceContact
      }
    })
  },

  contactsTableTotalCount: (state) => {
    return state.contactsTableTotalCount
  },

  contactsTableItemsForMerge: (state) => {
    return Object.values(state.contactsTableItemsForMerge).map(item => {
      const displayPhones = []
      if (item.phones && item.phones.length) {
        item.phones.forEach(itemPhone => {
          let name = ''
          switch (itemPhone.phoneType.name) {
            case phoneType.mobile:
              name = i18n.t('phoneType.mobile')
              break
            case phoneType.work:
              name = i18n.t('phoneType.work')
              break
            case phoneType.home:
              name = i18n.t('phoneType.home')
              break
          }
          displayPhones.push(`${itemPhone.phone} (${name})`)
        })
      }
      return {
        ...item,
        avatarChar: getAvatarChar(item.firstName, item.lastName, item.company, item.email?.email),
        displayEmail: item.email?.email,
        displayPhones
      }
    })
  },

  contactsTableTotalCountForMerge: (state) => {
    return state.contactsTableTotalCountForMerge
  },

  duplicatesTableItems: (state) => {
    return Object.values(state.duplicatesTableItems).map(item => {
      return {
        ...item,
        avatarChar: getAvatarChar(item.firstName, item.lastName, item.company, item.email?.email),
        displayEmail: item.email?.email,
        displayPhone: item.phone?.phone
      }
    })
  },

  duplicatesTableTotalCount: (state) => {
    return state.duplicatesTableTotalCount
  },

  mergeContactsTableItems: (state) => {
    return Object.values(state.contactsTableItems).map(item => {
      const isDuplicate = !!state.duplicatesTableItems[item.id]
      return {
        ...item,
        merge: isDuplicate,
        avatarChar: getAvatarChar(item.firstName, item.lastName, item.company, item.email?.email),
        displayEmail: item.email?.email,
        displayPhone: item.phone?.phone
      }
    })
  },

  mergeContactsTableTotalCount: (state) => {
    return state.mergeContactsTableTotalCount
  },

  phoneContacts: (state) => {
    return state.phoneContacts.map(item => {
      return {
        ...item,
        contactId: item.id,
        phoneNumber: item.phone
      }
    })
  },

  phoneContactsMobile: (state) => {
    return state.phoneContactsMobile.map(item => {
      return {
        ...item,
        contactId: item.id,
        phoneNumber: item.phone
      }
    })
  },

  shortContactById: (state) => (id) => {
    return state.shortContacts[id]
  },

  contactById: (state) => (id) => {
    return state.contactsById[id]
  },

  shortContactsForProject: (state, getters, rootState, rootGetters) => (projectId) => {
    const projectMembers = rootGetters['member/membersByProject'](projectId)
    const list = Object.values(state.shortContacts).map(contact => {
      return {
        ...contact,
        isMember: !!projectMembers.find(x => x.contactId === contact.id),
        contactId: contact.id
      }
    })

    return list.sort((x, y) => {
      if (x.isMember === y.isMember) {
        if (x.name < y.name) return -1
        if (x.name > y.name) return 1
        return 0
      }
      if (x.isMember) return -1
      return 1
    })
  }
}


const actions = {
  async putEncryptionPassword ({ commit, rootState }, payload) {
    const response = await contactApi.putEncryptionPassword(rootState.tenant.current, payload)
    commit('SET_CONTACT_BY_ID', response.data)
    return response.data
  },

  async checkUniqueEmail ({ rootState }, email) {
    const response = await contactApi.getUniqueEmail(rootState.tenant.current, email)
    return response.data
  },

  async loadContactsTableItems ({ commit, rootState }, { skip, take, search }) {
    const response = await contactApi.getContacts(rootState.tenant.current, skip, take, search)
    commit('SET_CONTACTS_TABLE_ITEMS', { contacts: response.data, totalCount: parseInt(response.headers['x-total-count']) })
  },

  async loadContactsTableItemsForMerge ({ commit, rootState }, { skip, take, search }) {
    const response = await contactApi.getContacts(rootState.tenant.current, skip, take, search)
    commit('SET_CONTACTS_TABLE_ITEMS_FOR_MERGE', { contacts: response.data, totalCount: parseInt(response.headers['x-total-count']) })
  },

  async loadDuplicates ({ commit, rootState }, id) {
    const response = await contactApi.getDuplicates(rootState.tenant.current, id)
    commit('SET_DUPLICATES_TABLE_ITEMS', { duplicates: response.data, totalCount: parseInt(response.headers['x-total-count']) })
    return response.data
  },

  async loadShortContacts ({ commit, rootState }) {
    const response = await contactApi.getContactsShort(rootState.tenant.current)
    commit('SET_SHORT_CONTACTS', response.data)
  },

  async loadPhoneContacts ({ commit, rootState }) {
    const response = await contactApi.getContactsPhones(rootState.tenant.current)
    commit('SET_PHONE_CONTACTS', response.data)
  },

  async loadPhoneContactsMobile ({ commit, rootState }) {
    const response = await contactApi.getContactsPhones(rootState.tenant.current)
    commit('SET_PHONE_CONTACTS_MOBILE', response.data)
  },

  async loadContactById ({ commit, rootState }, id) {
    const response = await contactApi.getContactById(rootState.tenant.current, id)
    commit('SET_CONTACT_BY_ID', response.data)
    return response.data
  },

  async loadAndUpdateContact ({ commit, dispatch, rootState }, entityData) {
    const response = await contactApi.getContactById(rootState.tenant.current, entityData.entityId)
    commit('UPDATE_CONTACT', response.data)
    dispatch('contactLabel/loadContactLabels', null, { root: true })
    return response.data
  },

  async loadRelations ({ commit, rootState }, id) {
    const response = await contactApi.getRelations(rootState.tenant.current, id)
    return response.data
  },

  async mergeContact ({ commit, rootState, dispatch }, contact) {
    contact.color = getRandomAvatarColor()
    const response = await contactApi.mergeContact(rootState.tenant.current, contact)
    commit('CREATE_CONTACT', response.data)
    dispatch('contactLabel/loadContactLabels', null, { root: true })
    return response.data
  },

  async createContact ({ commit, rootState, dispatch }, contact) {
    contact.color = getRandomAvatarColor()
    const response = await contactApi.createContact(rootState.tenant.current, contact)
    commit('CREATE_CONTACT', response.data)
    dispatch('contactLabel/loadContactLabels', null, { root: true })
    return response.data
  },

  async updateContact ({ commit, rootState, dispatch }, contact) {
    const response = await contactApi.updateContact(rootState.tenant.current, contact.id, contact)
    commit('UPDATE_CONTACT', response.data)
    dispatch('contactLabel/loadContactLabels', null, { root: true })
    return response.data
  },


  async deleteContact ({ commit, rootState }, { id }) {
    await contactApi.deleteContact(rootState.tenant.current, id)
    commit('DELETE_CONTACT', id)
  },


  async addLabelToContact ({ commit, rootState }, { contactId, labelId }) {
    await contactApi.addLabelToContact(rootState.tenant.current, contactId, labelId)
    commit('ADD_CONTACT_LABEL', { contactId, labelId })
  },

  async removeLabelFromContact ({ commit, rootState }, { contactId, labelId }) {
    await contactApi.removeLabelFromContact(rootState.tenant.current, contactId, labelId)
    commit('REMOVE_CONTACT_LABEL', { contactId, labelId })
  },

  removeLabelFromAllContacts ({ commit }, labelId) {
    const contacts = Object.values(state.contactsTableItems)
    for (const contact of contacts) {
      commit('REMOVE_CONTACT_LABEL', { contactId: contact.id, labelId })
    }
  },


  async loadAddressTypes ({ commit, rootState }) {
    const response = await contactApi.getAddressTypes(rootState.tenant.current)
    commit('SET_ADDRESS_TYPES', response.data)
  },

  async loadPhoneTypes ({ commit, rootState }) {
    const response = await contactApi.getPhoneTypes(rootState.tenant.current)
    commit('SET_PHONE_TYPES', response.data)
  },

  async loadEmailTypes ({ commit, rootState }) {
    const response = await contactApi.getEmailTypes(rootState.tenant.current)
    commit('SET_EMAIL_TYPES', response.data)
  }
}


const mutations = {
  SET_CONTACTS_TABLE_ITEMS (state, { contacts, totalCount }) {
    state.contactsTableItems = {}
    for (const contact of contacts) {
      Vue.set(state.contactsTableItems, contact.id, contact)
    }
    state.contactsTableTotalCount = totalCount
  },

  SET_CONTACTS_TABLE_ITEMS_FOR_MERGE (state, { contacts, totalCount }) {
    state.contactsTableItemsForMerge = {}
    for (const contact of contacts) {
      Vue.set(state.contactsTableItemsForMerge, contact.id, contact)
    }
    state.contactsTableTotalCountForMerge = totalCount
  },

  SET_DUPLICATES_TABLE_ITEMS (state, { duplicates, totalCount }) {
    state.duplicatesTableItems = {}
    for (const duplicate of duplicates) {
      Vue.set(state.duplicatesTableItems, duplicate.id, duplicate)
    }
    state.duplicatesTableTotalCount = totalCount
  },

  SET_SHORT_CONTACTS (state, shortContacts) {
    state.shortContacts = {}
    for (const contact of shortContacts) {
      Vue.set(state.shortContacts, contact.id, contact)
    }
  },

  SET_PHONE_CONTACTS (state, data) {
    state.phoneContacts = data
  },

  SET_PHONE_CONTACTS_MOBILE (state, data) {
    state.phoneContactsMobile = data.map(el => {
      let phone = null
      if (el.phones?.length) {
        const mobilePhones = el.phones.filter(phone => phone.phoneType.name === phoneType.mobile)
        if (mobilePhones.length) {
          phone = mobilePhones[0].phone
        }
      }
      let displayNameWithCompanyAndMobile = el.displayNameWithCompany
      if (!phone) {
        displayNameWithCompanyAndMobile += i18n.t('sms.noMobilePhone')
      }

      return {
        id: el.id,
        displayNameWithCompanyAndMobile,
        color: el.color,
        phone,
        disabled: !phone
      }
    })
  },

  SET_CONTACT_BY_ID (state, contact) {
    Vue.set(state.contactsById, contact.id, contact)
  },

  CREATE_CONTACT (state, contact) {
    state.contactsTableTotalCount = state.contactsTableTotalCount + 1
    Vue.set(state.contactsTableItems, contact.id, contact)
    Vue.set(state.shortContacts, contact.id, contact)
  },

  UPDATE_CONTACT (state, contact) {
    Vue.set(state.contactsTableItems, contact.id, contact)
    Vue.set(state.shortContacts, contact.id, contact)
  },

  DELETE_CONTACT (state, id) {
    state.contactsTableTotalCount = state.contactsTableTotalCount - 1
    Vue.delete(state.contactsTableItems, id)
    Vue.delete(state.shortContacts, id)
  },


  ADD_CONTACT_LABEL (state, { contactId, labelId }) {
    state.contactsTableItems[contactId].labels.push(labelId)
  },

  REMOVE_CONTACT_LABEL (state, { contactId, labelId }) {
    const labelIndex = state.contactsTableItems[contactId].labels.findIndex(x => x === labelId)
    if (labelIndex !== -1) {
      state.contactsTableItems[contactId].labels.splice(labelIndex, 1)
    }
  },


  SET_ADDRESS_TYPES (state, data) {
    state.addressTypes = data
  },

  SET_EMAIL_TYPES (state, data) {
    state.emailTypes = data
  },

  SET_PHONE_TYPES (state, data) {
    state.phoneTypes = data
  }
}


export default {
  state,
  getters,
  actions,
  mutations
}
